import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AppError } from '@/core/errors/AppError';
import { PromotorFormSchema, } from '@/data/dto/promotor';
import { TIPOS_DOCUMENTO } from '@/data/dto/cliente';
import { useCrearPromotor, useEditarPromotor } from '@/data/hooks/usePromotoresAdmin';
import { Button } from '@/shared/components/Button';
import { Field } from '@/shared/components/Field';
import { Input } from '@/shared/components/Input';
import { PhoneInput } from '@/shared/components/PhoneInput';
import { Select } from '@/shared/components/Select';
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, } from '@/shared/components/Modal';
import { useToast } from '@/shared/components/Toast';
const FRECUENCIAS = ['DIARIA', 'SEMANAL', 'QUINCENAL', 'MENSUAL'];
const DEFAULT = {
    nombre: '',
    apellido: '',
    tipoDocumento: '',
    nroDocumento: '',
    email: '',
    telefono: '',
    pctOverrideEfectivo: '',
    pctOverrideTarjeta: '',
    frecuenciaLiquidacion: '',
};
export function PromotorFormDialog({ open, onOpenChange, promotor }) {
    const modoEdicion = Boolean(promotor);
    const toast = useToast();
    const crear = useCrearPromotor();
    const editar = useEditarPromotor();
    const isPending = crear.isPending || editar.isPending;
    const form = useForm({
        resolver: zodResolver(PromotorFormSchema),
        defaultValues: DEFAULT,
    });
    useEffect(() => {
        if (!open)
            return;
        form.reset(promotor
            ? {
                nombre: promotor.nombre,
                apellido: promotor.apellido ?? '',
                tipoDocumento: (promotor.tipoDocumento ?? ''),
                nroDocumento: promotor.nroDocumento ?? '',
                email: promotor.email ?? '',
                telefono: promotor.telefono ?? '',
                pctOverrideEfectivo: promotor.pctOverrideEfectivo != null ? String(promotor.pctOverrideEfectivo) : '',
                pctOverrideTarjeta: promotor.pctOverrideTarjeta != null ? String(promotor.pctOverrideTarjeta) : '',
                frecuenciaLiquidacion: promotor.frecuenciaLiquidacion ?? '',
            }
            : DEFAULT);
    }, [open, promotor, form]);
    const onSubmit = form.handleSubmit((values) => {
        const onSuccess = () => {
            toast.exito(modoEdicion ? 'Promotor actualizado' : 'Promotor creado');
            onOpenChange(false);
        };
        const onError = (err) => {
            if (err instanceof AppError && err.errores) {
                for (const e of err.errores) {
                    form.setError(e.campo, { message: e.mensaje });
                }
                return;
            }
            toast.error(err instanceof AppError ? err.mensaje : 'No pudimos guardar.');
        };
        if (modoEdicion && promotor) {
            editar.mutate({ id: promotor.id, values }, { onSuccess, onError });
        }
        else {
            crear.mutate(values, { onSuccess, onError });
        }
    });
    const errors = form.formState.errors;
    return (_jsx(Modal, { open: open, onOpenChange: onOpenChange, children: _jsxs(ModalContent, { size: "md", children: [_jsx(ModalHeader, { titulo: modoEdicion ? 'Editar promotor' : 'Nuevo promotor', descripcion: "Los promotores son personas o entidades que traen reservas y cobran comisi\u00F3n." }), _jsxs("form", { onSubmit: onSubmit, noValidate: true, children: [_jsx(ModalBody, { children: _jsxs("div", { className: "grid grid-cols-1 gap-3 sm:grid-cols-2", children: [_jsx(Field, { label: "Nombre / Raz\u00F3n social", error: errors.nombre?.message, className: "sm:col-span-2", required: true, children: _jsx(Input, { autoFocus: true, ...form.register('nombre') }) }), _jsx(Field, { label: "Apellido (si es persona)", error: errors.apellido?.message, className: "sm:col-span-2", children: _jsx(Input, { ...form.register('apellido') }) }), _jsxs("div", { className: "sm:col-span-2 grid grid-cols-1 gap-3 sm:grid-cols-[160px_1fr]", children: [_jsx(Field, { label: "Tipo documento", error: errors.tipoDocumento?.message, children: _jsxs(Select, { ...form.register('tipoDocumento'), children: [_jsx("option", { value: "", children: "Sin especificar" }), TIPOS_DOCUMENTO.map((t) => (_jsx("option", { value: t, children: t }, t)))] }) }), _jsx(Field, { label: "N\u00BA documento", error: errors.nroDocumento?.message, children: _jsx(Input, { ...form.register('nroDocumento') }) })] }), _jsx(Field, { label: "Email", error: errors.email?.message, children: _jsx(Input, { type: "email", ...form.register('email') }) }), _jsx(Field, { label: "Tel\u00E9fono", error: errors.telefono?.message, children: _jsx(PhoneInput, { value: form.watch('telefono') ?? '', onChange: (v) => form.setValue('telefono', v, { shouldDirty: true }) }) }), _jsx(Field, { label: "Override % comisi\u00F3n (efectivo)", helper: "Si se define, reemplaza la comisi\u00F3n base en pagos efectivo", error: errors.pctOverrideEfectivo?.message, children: _jsx(Input, { inputMode: "decimal", placeholder: "15", ...form.register('pctOverrideEfectivo') }) }), _jsx(Field, { label: "Override % comisi\u00F3n (tarjeta)", helper: "Si se define, reemplaza la comisi\u00F3n base en pagos con tarjeta", error: errors.pctOverrideTarjeta?.message, children: _jsx(Input, { inputMode: "decimal", placeholder: "12", ...form.register('pctOverrideTarjeta') }) }), _jsx(Field, { label: "Frecuencia liquidaci\u00F3n", className: "sm:col-span-2", children: _jsxs(Select, { ...form.register('frecuenciaLiquidacion'), children: [_jsx("option", { value: "", children: "Por defecto" }), FRECUENCIAS.map((f) => (_jsx("option", { value: f, children: f }, f)))] }) })] }) }), _jsxs(ModalFooter, { children: [_jsx(Button, { type: "button", variant: "secondary", onClick: () => onOpenChange(false), disabled: isPending, children: "Cancelar" }), _jsx(Button, { type: "submit", cargando: isPending, children: modoEdicion ? 'Guardar cambios' : 'Crear promotor' })] })] })] }) }));
}
